import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  setApiToken,
  setApiTokenReady,
  setActivities,
  setMembershipData,
  SetFeaturesToBlock,
  setSubscriptionNextAction,
  setFileResume,
  setJobResumeUpload,
  setProfileData,
  setResumeJson,
} from "../store/actions/actions";

/*
 * Replicates the data bootstrap the legacy Header.js performed for every
 * authenticated screen: publish the API token to the profile store, then
 * load membership/resume/feature-flags and recent activity once the token
 * is ready. Endpoints, payloads and dispatch order are identical to legacy.
 */
export default function useAppBootstrap() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userEmail = useSelector((s) => s.auth.auth.email);
  const accesstoken = useSelector((s) => s.auth.auth.accesstoken);
  const apiToken = useSelector((s) => s.profile.apiToken);
  const apiTokenReady = useSelector((s) => s.profile.apiTokenReady);
  const membershipData = useSelector((s) => s.profile.membershipData);
  const ranRef = useRef(false);

  useEffect(() => {
    if (userEmail !== "") {
      /* the access token is already in the auth store from login/localStorage,
         so publish it and mark it ready immediately — the legacy 1s delay was
         dead waiting time on every authenticated page load */
      dispatch(setApiToken(accesstoken));
      dispatch(setApiTokenReady(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  useEffect(() => {
    if (location.pathname === "/resume-upload") return;
    if (apiTokenReady === false || apiToken === "" || ranRef.current) return;
    ranRef.current = true;

    const fetchMembership = async () => {
      if (membershipData.length > 0) return;
      try {
        const response = await fetch(
          "https://us-east1-foursssolutions.cloudfunctions.net/send_resume_details_front_end_v2",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${apiToken}`,
            },
            body: JSON.stringify({ emailid: userEmail }),
          }
        );
        const data = await response.json();
        /* parsing kept byte-identical to legacy Header.js — no defensive
           fallbacks: a missing file_details throws into the catch (legacy
           behavior: nothing gets dispatched on a malformed response) */
        const extractedFilenames = data.file_details.map((f) => f.file_name);
        dispatch(setMembershipData(data.product_details));
        dispatch(SetFeaturesToBlock(data.features_to_block));
        dispatch(setSubscriptionNextAction(data.subscription_next_action));
        dispatch(setFileResume(extractedFilenames[0]));
        /* prefetch the profile record into the store so Profile/Dashboard render
           from it instantly instead of each re-fetching this same call */
        if (Array.isArray(data.user_details) && data.user_details[0]) {
          dispatch(setProfileData(data.user_details[0]));
        }
        /* the parsed resume, so the tailoring flow can show what it is about
           to change and diff the result. Stored as a string by the DB. */
        if (data.resume_json) {
          try {
            dispatch(
              setResumeJson(
                typeof data.resume_json === "string" ? JSON.parse(data.resume_json) : data.resume_json
              )
            );
          } catch (e) {
            /* malformed resume json must never break app bootstrap */
          }
        }
        if (data.show_resume_upload === true) {
          if (location.pathname === "/job/detailed") {
            dispatch(setJobResumeUpload(true));
          } else {
            navigate("/resume-upload");
          }
        }
        /* legacy quirk kept: the ok-check runs AFTER the dispatches */
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Bootstrap membership error:", err);
      }
    };

    const fetchActivities = async () => {
      try {
        const response = await fetch(
          "https://us-east1-foursssolutions.cloudfunctions.net/user_recent_activities_v2",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer",
            },
            body: JSON.stringify({ email_id: userEmail, task: "recent_activity" }),
          }
        );
        if (!response.ok) throw new Error("Failed to fetch activities");
        const data = await response.json();
        dispatch(setActivities(data.recent_activity));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Bootstrap activities error:", err);
      }
    };

    fetchMembership();
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenReady, apiToken]);
}
